import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent
} from "@tanstack/react-router";

import { RootLayout } from "@/routes/RootLayout";
import { ClipPage } from "@/routes/ClipPage";
import { LandingPage } from "@/routes/LandingPage";
import { NotFoundPage } from "@/routes/NotFoundPage";

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage
});

/**
 * `/v?p=<bucket>&c=<clip>` — the share link.
 *
 * Both parameters are carried in the URL rather than looked up, so a link is
 * self-contained and the site stays a bag of static files. Validation happens
 * in the page (which can render a proper error) rather than here, where a
 * throw would surface as a router crash.
 */
export const clipRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/v",
  validateSearch: (search: Record<string, unknown>): { p?: string; c?: string } => ({
    p: typeof search.p === "string" ? search.p : undefined,
    c: typeof search.c === "string" ? search.c : undefined
  }),
  component: ClipPage
});

/**
 * Dev-only `/__preview`: the clip page against fixture data. Built with a
 * dynamic import so the fixture and its sample assets never reach a production
 * bundle — `import.meta.env.DEV` is statically false there, and Rollup drops
 * the whole branch.
 */
const devRoutes = import.meta.env.DEV
  ? [
      createRoute({
        getParentRoute: () => rootRoute,
        path: "/__preview",
        component: lazyRouteComponent(() => import("@/dev/PreviewPage"), "PreviewPage")
      })
    ]
  : [];

const routeTree = rootRoute.addChildren([indexRoute, clipRoute, ...devRoutes]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
